import { OktaAuth, OktaAuthOptions } from "@okta/okta-auth-js";
import {
  PublicClientApplication,
  Configuration,
  AuthenticationResult,
} from "@azure/msal-browser";
import { config } from "../config";

export interface User {
  sub?: string;
  name?: string;
  email?: string;
  preferred_username?: string;
  [key: string]: any;
}

export interface AuthService {
  initialize(): Promise<void>;
  isAuthenticated(): Promise<boolean>;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
  getUserInfo(): Promise<User | null>;
  getAuthToken(): Promise<string | null>;
}

class OktaAuthService implements AuthService {
  private oktaAuth: OktaAuth;

  constructor() {
    const oktaAuthConfig: OktaAuthOptions = {
      issuer: config.auth.okta.issuer as string,
      clientId: config.auth.okta.clientId as string,
      redirectUri: config.auth.okta.redirectUri as string,
      postLogoutRedirectUri: config.auth.okta.postLogoutRedirectUri as string,
      scopes: config.auth.scopes,
      pkce: true,
      tokenManager: {
        storage: "sessionStorage",
      },
      cookies: {
        secure: false,
      },
    };

    this.oktaAuth = new OktaAuth({
      ...oktaAuthConfig,
      responseType: "code",
    });
  }

  async initialize(): Promise<void> {
    await this.oktaAuth.start();
  }

  async isAuthenticated(): Promise<boolean> {
    return await this.oktaAuth.isAuthenticated();
  }

  async signIn(): Promise<void> {
    const authParams = {
      scopes: config.auth.scopes,
      redirectUri: config.auth.okta.redirectUri as string,
    };
    await this.oktaAuth.signInWithRedirect(authParams);
  }

  async signOut(): Promise<void> {
    try {
      this.oktaAuth.tokenManager.clear();
      const signOutOptions = {
        postLogoutRedirectUri: config.auth.okta.postLogoutRedirectUri,
      };
      await this.oktaAuth.signOut(signOutOptions);
    } catch (error) {
      window.location.href = "/";
    }
  }

  async getUserInfo(): Promise<User | null> {
    if (await this.isAuthenticated()) {
      return await this.oktaAuth.getUser();
    }
    return null;
  }

  async getAuthToken(): Promise<string | null> {
    if (await this.isAuthenticated()) {
      const token = this.oktaAuth.getAccessToken();
      return token || null;
    }
    return null;
  }

  getOktaAuth(): OktaAuth {
    return this.oktaAuth;
  }
}

class AzureAuthService implements AuthService {
  private msalInstance: PublicClientApplication;
  private isLoggingOut: boolean = false;

  constructor() {
    const msalConfig: Configuration = {
      auth: {
        clientId: config.auth.azure.clientId as string,
        authority: `https://login.microsoftonline.com/common`,
        redirectUri: config.auth.azure.redirectUri as string,
        postLogoutRedirectUri: config.auth.azure
          .postLogoutRedirectUri as string,
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
      },
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  async initialize(): Promise<void> {
    await this.msalInstance.initialize();

    // Handle redirect response if returning from login
    try {
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        // Successfully authenticated via redirect
        console.log("Azure AD redirect authentication successful");
      }
    } catch (error) {
      console.error("Error handling Azure AD redirect:", error);
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0;
  }

  async signIn(): Promise<void> {
    if (this.isLoggingOut) {
      console.warn("Azure AD signIn called during logout - ignoring");
      return;
    }

    try {
      const loginRequest = {
        scopes: config.auth.scopes,
        redirectUri: config.auth.azure.redirectUri as string,
        prompt: "select_account", // Force account selection to prevent auto-login
      };
      this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Azure AD login error:", error);
      throw error;
    }
  }

  async signOut(): Promise<void> {
    try {
      this.isLoggingOut = true;

      // Clear MSAL cache and local storage immediately
      try {
        await this.msalInstance.clearCache();
      } catch (cacheError) {
        console.warn("Error clearing MSAL cache:", cacheError);
      }

      // Clear all browser storage
      sessionStorage.clear();
      localStorage.clear();

      // Build manual logout URL
      const tenantId = config.auth.azure.tenantId;
      const clientId = config.auth.azure.clientId;
      const postLogoutRedirectUri = encodeURIComponent(
        config.auth.azure.postLogoutRedirectUri as string,
      );

      // Use Azure AD logout endpoint that forces complete logout
      const logoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?client_id=${clientId}&post_logout_redirect_uri=${postLogoutRedirectUri}`;

      // Direct redirect to Azure AD logout
      window.location.href = logoutUrl;
    } catch (error) {
      console.error("Azure AD logout error:", error);
      // Ultimate fallback - just clear everything and go home
      try {
        sessionStorage.clear();
        localStorage.clear();
      } catch (storageError) {
        console.warn("Error clearing storage:", storageError);
      }
      window.location.href = "/";
    } finally {
      this.isLoggingOut = false;
    }
  }

  async getUserInfo(): Promise<User | null> {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      return {
        sub: account.localAccountId,
        name: account.name || "",
        email: account.username,
        preferred_username: account.username,
      };
    }
    return null;
  }

  async getAuthToken(): Promise<string | null> {
    try {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const silentRequest = {
          scopes: config.auth.scopes,
          account: accounts[0],
        };
        const response: AuthenticationResult =
          await this.msalInstance.acquireTokenSilent(silentRequest);
        return response.accessToken;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  getMsalInstance(): PublicClientApplication {
    return this.msalInstance;
  }
}

function createAuthService(): AuthService {
  const provider = config.auth.provider;

  switch (provider) {
    case "azure":
      return new AzureAuthService();
    case "okta":
      return new OktaAuthService();
    default:
      return new OktaAuthService();
  }
}

export const authService = createAuthService();

export const oktaAuthService =
  authService instanceof OktaAuthService ? authService : null;
export const azureAuthService =
  authService instanceof AzureAuthService ? authService : null;

export const oktaAuth = oktaAuthService?.getOktaAuth();
export const msalInstance = azureAuthService?.getMsalInstance();

export const isAuthenticated = () => authService.isAuthenticated();
export const signIn = () => authService.signIn();
export const signOut = () => authService.signOut();
export const getUserInfo = () => authService.getUserInfo();
export const getAuthToken = () => authService.getAuthToken();

export const cleanAuthParams = (): void => {
  if (window.history && window.history.replaceState) {
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }
};
